import React, { memo } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/auth';
import { usePermissions } from '../../context/permissions';
import Navbar from './Navbar';
import Footer from './Footer';

// Lightweight loading component to reduce render cost
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
  </div>
);

// Memoized ProtectedRoute component to prevent unnecessary rerenders
export const ProtectedRoute = memo(({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  // Show minimal loading component if still initializing
  if (loading) {
    return <LoadingSpinner />;
  }
  
  // Quick check for authentication
  return isAuthenticated ? children : <Navigate to="/" replace />;
});

// Optimized AdminRoute component using the PermissionsProvider
export const AdminRoute = memo(({ children, route }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { hasPermission, loading: permissionsLoading, isSuperAdmin } = usePermissions();
  
  // Only show loading if auth is still loading (permissions loading shouldn't block UI)
  const isLoading = authLoading;
  
  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log(`AdminRoute check for route: ${route}`);
    console.log(`isAuthenticated: ${isAuthenticated}, isSuperAdmin: ${isSuperAdmin}`);
    console.log(`hasPermission: ${hasPermission(route)}`);
  }
  
  // Show loading state only for auth loading
  if (isLoading) {
    return <LoadingSpinner />;
  }
  
  // First, check basic authentication
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  // Super admins get access to everything
  if (isSuperAdmin) {
    return children;
  }
  
  // Check permission for the specific route
  if (hasPermission(route)) {
    return children;
  }
  
  // Redirect to dashboard if user doesn't have permission
  return <Navigate to="/dashboard" replace />;
});

// Lightweight and memoized public layout
export const PublicLayout = () => {
	return (
		<div className="flex flex-col min-h-screen bg-gray-50">
			<Navbar />
			<main className="flex-grow">
				<Outlet />
			</main>
			<Footer />
		</div>
	);
};
